import React, { useCallback, useEffect, useRef, useState } from "react";
import FreelancerProfileEditor from "../components/FreelancerProfileEditor";
import { findJobs, generateProposal, getFreelancerProfile, listFreelancerProfiles, api, markProposal, getProposalStats } from "../api";
import { useSubscription } from "../SubscriptionContext";
import UsageBadge from "../components/UsageBadge";

function apiErrorMessage(err) {
  const d = err.response?.data;
  if (typeof d?.error === "string") return d.error;
  if (d?.error && typeof d.error === "object") return JSON.stringify(d.error);
  return err.message || "Something went wrong";
}

const PROPOSAL_TONES = [
  { id: "professional", label: "Professional", desc: "Formal and polished" },
  { id: "friendly", label: "Friendly", desc: "Warm and approachable" },
  { id: "confident", label: "Confident", desc: "Bold and assertive" },
  { id: "consultative", label: "Consultative", desc: "Advisory and expert" },
];

const PROPOSAL_LENGTHS = [
  { id: "concise", label: "Concise", words: "150-180", desc: "Quick pitch" },
  { id: "standard", label: "Standard", words: "250-350", desc: "Balanced" },
  { id: "detailed", label: "Detailed", words: "400-500", desc: "In-depth" },
];

const FOCUS_AREAS = [
  { id: "experience", label: "Experience & Portfolio" },
  { id: "approach", label: "Technical Approach" },
  { id: "value", label: "Value & ROI" },
  { id: "speed", label: "Fast Delivery" },
  { id: "quality", label: "Quality Focus" },
  { id: "budget", label: "Budget-Friendly" },
];

export default function ProposalsTab() {
  const { subscription, refresh: refreshSubscription } = useSubscription();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [proposal, setProposal] = useState("");
  const [proposalSource, setProposalSource] = useState(null); // AI model that generated it
  const [msg, setMsg] = useState("");
  const [msgTone, setMsgTone] = useState("info");
  const [searchQuery, setSearchQuery] = useState("");
  const [jobPlatform, setJobPlatform] = useState("all");
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [, setProfileData] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);

  // New customization states
  const [proposalTone, setProposalTone] = useState("professional");
  const [proposalLength, setProposalLength] = useState("standard");
  const [focusAreas, setFocusAreas] = useState(["experience"]);
  const [customNotes, setCustomNotes] = useState("");
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProposal, setEditedProposal] = useState("");

  // Custom job URL states
  const [customJobUrl, setCustomJobUrl] = useState("");
  const [analyzingUrl, setAnalyzingUrl] = useState(false);
  const [showCustomJob, setShowCustomJob] = useState(false);

  // Win/loss tracking
  const [proposalStatuses, setProposalStatuses] = useState({}); // { jobId: "won"|"lost"|"pending" }
  const [winStats, setWinStats] = useState({ total: 0, won: 0, lost: 0, win_rate: 0 });

  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const jobPlatformRef = useRef(jobPlatform);
  jobPlatformRef.current = jobPlatform;

  const showMsg = (text, tone = "info") => {
    setMsg(text);
    setMsgTone(tone);
    if (tone === "success" || tone === "error") {
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const handleJobSearch = useCallback(async () => {
    setLoadingJobs(true);
    showMsg("Searching for jobs…", "info");
    try {
      const res = await findJobs(searchQueryRef.current, jobPlatformRef.current);
      const data = res.data;
      if (!Array.isArray(data)) {
        const errText = typeof data?.error === "string" ? data.error : "Could not load job listings.";
        setJobs([]);
        setSelectedJob(null);
        showMsg(errText, "error");
        return;
      }
      setJobs(data);
      setSelectedJob(data[0] || null);
      showMsg(data.length > 0 ? `${data.length} jobs found` : "No jobs found for this query.", data.length > 0 ? "success" : "info");
    } catch (err) {
      setJobs([]);
      setSelectedJob(null);
      showMsg(apiErrorMessage(err), "error");
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    handleJobSearch();
    checkProfile(); // eslint-disable-line react-hooks/exhaustive-deps
    fetchWinStats(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [handleJobSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkProfile = async () => {
    try {
      const { data } = await getFreelancerProfile();
      if (data && data.id) {
        setHasProfile(true);
        setProfileData(data);
        if (!selectedProfileId) setSelectedProfileId(data.id);
        if (data.proposal_tone) setProposalTone(data.proposal_tone);
      }
    } catch (err) {
      setHasProfile(false);
    }
    try {
      const { data: profiles } = await listFreelancerProfiles();
      if (Array.isArray(profiles)) setAllProfiles(profiles);
    } catch (err) { /* ignore */ }
  };

  const fetchWinStats = async () => {
    try {
      const { data } = await getProposalStats();
      setWinStats(data);
    } catch (err) { /* ignore – stats are non-critical */ }
  };

  const handleMarkProposal = async (jobId, status) => {
    // Optimistic UI update
    setProposalStatuses(prev => ({ ...prev, [jobId]: status }));
    try {
      await markProposal(String(jobId), status);
      await fetchWinStats();
    } catch (err) {
      // Roll back on failure
      setProposalStatuses(prev => ({ ...prev, [jobId]: undefined }));
      showMsg("Could not save proposal status.", "error");
    }
  };

  // State for manual job input when URL scraping fails
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualJobTitle, setManualJobTitle] = useState("");
  const [manualJobDescription, setManualJobDescription] = useState("");
  const [manualJobBudget, setManualJobBudget] = useState("");
  const [manualPlatform, setManualPlatform] = useState("");

  const analyzeCustomJobUrl = async () => {
    if (!customJobUrl.trim() || !customJobUrl.startsWith("http")) {
      showMsg("Please enter a valid job posting URL", "error");
      return;
    }
    setAnalyzingUrl(true);
    showMsg("Analyzing job posting with AI...", "info");
    try {
      const { data } = await api.post("/proposals/analyze-job-url", { url: customJobUrl });
      if (data.error) {
        showMsg(data.error, "error");
        return;
      }

      // Check if manual input is needed (site blocked scraping)
      if (data.needs_manual_input) {
        setManualPlatform(data.platform || "Job Board");
        setShowManualInput(true);
        showMsg(`${data.platform} requires login. Please paste the job details below.`, "info");
        return;
      }

      const analyzedJob = {
        id: `custom-${Date.now()}`,
        title: data.title || "Custom Job",
        description: data.description || "",
        budget: data.budget || "",
        platform_name: data.platform || "Custom",
        url: customJobUrl,
        skills: data.skills || [],
        requirements: data.requirements || [],
      };
      setSelectedJob(analyzedJob);
      setJobs(prev => [analyzedJob, ...prev.filter(j => !j.id.toString().startsWith("custom-"))]);
      setShowJobDetails(true);
      setShowManualInput(false);
      showMsg(`Job analyzed: "${analyzedJob.title}"`, "success");
    } catch (err) {
      // On network error, offer manual input
      setManualPlatform("Job Board");
      setShowManualInput(true);
      showMsg("Could not fetch the job page. Please paste the job details manually.", "info");
    } finally {
      setAnalyzingUrl(false);
    }
  };

  const handleManualJobSubmit = () => {
    if (!manualJobTitle.trim()) {
      showMsg("Please enter a job title", "error");
      return;
    }
    const manualJob = {
      id: `manual-${Date.now()}`,
      title: manualJobTitle,
      description: manualJobDescription,
      budget: manualJobBudget || "Not specified",
      platform_name: manualPlatform || "Custom",
      url: customJobUrl,
    };
    setSelectedJob(manualJob);
    setJobs(prev => [manualJob, ...prev.filter(j => !j.id.toString().startsWith("manual-") && !j.id.toString().startsWith("custom-"))]);
    setShowJobDetails(true);
    setShowManualInput(false);
    setManualJobTitle("");
    setManualJobDescription("");
    setManualJobBudget("");
    showMsg(`Job added: "${manualJob.title}"`, "success");
  };

  const runGenerateProposal = async (job) => {
    if (!job?.title) {
      showMsg("Select a job from the list first.", "error");
      return;
    }
    setLoadingProposal(true);
    setIsEditing(false);

    // Show what settings are being used
    const lengthLabel = proposalLength === "concise" ? "Concise (150-180 words)" : proposalLength === "detailed" ? "Detailed (400-500 words)" : "Standard (250-350 words)";
    showMsg(`Generating ${proposalTone} ${lengthLabel.toLowerCase()} proposal...`, "info");

    try {
      const { data } = await generateProposal(
        job.title,
        job.description || "",
        {
          tone: proposalTone,
          length: proposalLength,
          focus: focusAreas,
          customNotes: customNotes,
          budget: job.budget,
          platform: job.platform_name,
          profile_id: selectedProfileId,
        }
      );
      setProposal(data.proposal_text || "");
      setEditedProposal(data.proposal_text || "");
      setProposalSource(data.source || null);
      refreshSubscription();

      const actualWords = (data.proposal_text || "").split(/\s+/).filter(Boolean).length;
      showMsg(`Proposal ready! ${actualWords} words, ${proposalTone} tone.`, "success");
    } catch (err) {
      if (err.response?.status === 403) refreshSubscription();
      showMsg(apiErrorMessage(err), "error");
    } finally {
      setLoadingProposal(false);
    }
  };

  const handleCopyProposal = async () => {
    const textToCopy = isEditing ? editedProposal : proposal;
    if (!textToCopy.trim()) {
      showMsg("Nothing to copy yet — generate a proposal first.", "error");
      return;
    }
    await navigator.clipboard.writeText(textToCopy);
    showMsg("Copied to clipboard!", "success");
  };

  const handleProfileSave = () => {
    checkProfile();
    showMsg("Profile updated! Your proposals will now be personalized.", "success");
  };

  const toggleFocusArea = (id) => {
    setFocusAreas(prev =>
      prev.includes(id)
        ? prev.filter(a => a !== id)
        : [...prev, id].slice(0, 3)
    );
  };

  const bannerClass =
    msgTone === "error"
      ? "border-red-500/40 bg-red-950/50 text-red-100"
      : msgTone === "success"
        ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-50"
        : "border-white/10 bg-white/5 text-gray-200";

  const wordCount = (isEditing ? editedProposal : proposal).split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-br from-emerald-900/20 via-[#0d1117] to-cyan-900/20 p-6">
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Proposal Intelligence</h1>
                <p className="text-sm text-gray-400">AI-powered proposals that win clients</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {subscription?.usage?.proposals && (
              <UsageBadge label="Proposals" used={subscription.usage.proposals.used} limit={subscription.usage.proposals.limit} />
            )}
            <button
              onClick={() => setShowProfileEditor(true)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${
                hasProfile
                  ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/30"
                  : "bg-orange-500/20 border border-orange-500/30 text-orange-400 hover:bg-orange-500/30"
              }`}
            >
              {hasProfile ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Profile Ready
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Setup Profile
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Message Banner */}
      {msg && (
        <div className={`p-3 rounded-xl border text-sm flex items-center gap-2 ${bannerClass}`}>
          {msgTone === "success" && <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
          {msgTone === "error" && <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          {msg}
        </div>
      )}

      {/* Win/Loss Stats Bar */}
      <div className="flex items-center gap-4 px-4 py-3 rounded-xl border border-white/10 bg-[#0d1117] text-sm flex-wrap">
        <span className="text-gray-400 font-semibold">Proposal Outcomes</span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
          <span className="text-gray-300">Win Rate:</span>
          <span className="text-emerald-400 font-bold">{winStats.win_rate}%</span>
        </span>
        <span className="text-gray-700">|</span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
          <span className="text-gray-300">Won:</span>
          <span className="text-emerald-400 font-bold">{winStats.won}</span>
        </span>
        <span className="text-gray-700">|</span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
          <span className="text-gray-300">Lost:</span>
          <span className="text-red-400 font-bold">{winStats.lost}</span>
        </span>
        {winStats.total > 0 && (
          <>
            <span className="text-gray-700">|</span>
            <span className="text-gray-500 text-xs">({winStats.total} marked)</span>
          </>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Left Column: Job Search */}
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d1117] overflow-hidden">
            <div className="px-4 py-3 border-b border-white/8 bg-black/30">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <span className="text-lg">🔍</span>
                Find Jobs
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {/* Toggle between search and custom URL */}
              <div className="flex gap-1 p-1 rounded-xl bg-black/40 border border-white/10">
                <button
                  onClick={() => setShowCustomJob(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    !showCustomJob ? "bg-emerald-500/20 text-emerald-400" : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  Search Jobs
                </button>
                <button
                  onClick={() => setShowCustomJob(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                    showCustomJob ? "bg-cyan-500/20 text-cyan-400" : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  Paste Job URL
                </button>
              </div>

              {showCustomJob ? (
                /* Custom Job URL Input */
                <div className="space-y-3">
                  {!showManualInput ? (
                    <>
                      <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                        <p className="text-xs text-cyan-300 mb-2 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Paste any job posting URL
                        </p>
                        <p className="text-[10px] text-gray-500">Works with Upwork, Fiverr, LinkedIn, Indeed, or any job board</p>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={customJobUrl}
                          onChange={(e) => setCustomJobUrl(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && analyzeCustomJobUrl()}
                          placeholder="https://www.upwork.com/jobs/..."
                          className="flex-1 px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        />
                        <button
                          onClick={analyzeCustomJobUrl}
                          disabled={analyzingUrl || !customJobUrl.trim()}
                          className="px-4 py-2.5 rounded-xl bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-cyan-500 transition-colors flex items-center gap-1.5"
                        >
                          {analyzingUrl ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          )}
                          {analyzingUrl ? "..." : "Analyze"}
                        </button>
                      </div>
                      <button
                        onClick={() => setShowManualInput(true)}
                        className="w-full text-xs text-gray-500 hover:text-gray-300 py-2"
                      >
                        Or enter job details manually →
                      </button>
                    </>
                  ) : (
                    /* Manual Job Input Form */
                    <div className="space-y-3">
                      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <p className="text-xs text-amber-300 font-semibold flex items-center gap-1.5 mb-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Copy & Paste from {manualPlatform}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          1. Open the job on {manualPlatform} → 2. Copy the title & description → 3. Paste below
                        </p>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">Job Title *</label>
                        <input
                          type="text"
                          value={manualJobTitle}
                          onChange={(e) => setManualJobTitle(e.target.value)}
                          placeholder="e.g., Senior React Developer Needed"
                          className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">Job Description *</label>
                        <textarea
                          value={manualJobDescription}
                          onChange={(e) => setManualJobDescription(e.target.value)}
                          placeholder="Paste the full job description, requirements, and any other details here..."
                          className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 h-40 resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 uppercase tracking-wider mb-1">Budget (Optional)</label>
                        <input
                          type="text"
                          value={manualJobBudget}
                          onChange={(e) => setManualJobBudget(e.target.value)}
                          placeholder="e.g., $50-100/hr or $5,000 fixed"
                          className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowManualInput(false); setCustomJobUrl(""); }}
                          className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-400 text-sm font-semibold hover:bg-white/10 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleManualJobSubmit}
                          disabled={!manualJobTitle.trim() || !manualJobDescription.trim()}
                          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm font-semibold disabled:opacity-50 hover:from-amber-500 hover:to-orange-500 transition-colors"
                        >
                          Create Job →
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Regular Job Search */
                <>
                  <select
                    value={jobPlatform}
                    onChange={(e) => setJobPlatform(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  >
                    <optgroup label="All Sources">
                      <option value="all">All Platforms (21 sources)</option>
                    </optgroup>
                    <optgroup label="Freelance Marketplaces">
                      <option value="upwork">Upwork</option>
                      <option value="freelancer">Freelancer.com</option>
                      <option value="fiverr">Fiverr</option>
                      <option value="guru">Guru</option>
                      <option value="peopleperhour">PeoplePerHour</option>
                      <option value="twine">Twine</option>
                      <option value="workchest">WorkChest</option>
                    </optgroup>
                    <optgroup label="Remote Job Boards">
                      <option value="remoteok">RemoteOK</option>
                      <option value="remotive">Remotive</option>
                      <option value="weworkremotely">WeWorkRemotely</option>
                      <option value="jobicy">Jobicy</option>
                      <option value="arbeitnow">Arbeitnow (Europe)</option>
                    </optgroup>
                    <optgroup label="Tech & Startup">
                      <option value="builtin">BuiltIn</option>
                      <option value="aijobs">AI-Jobs</option>
                    </optgroup>
                    <optgroup label="Design & Creative">
                      <option value="dribbble">Dribbble</option>
                      <option value="archinect">Archinect</option>
                      <option value="dezeen">Dezeen</option>
                    </optgroup>
                    <optgroup label="Government">
                      <option value="usajobs">USAJOBS (Gov)</option>
                    </optgroup>
                    <optgroup label="Job Aggregators">
                      <option value="indeed">Indeed (RSS)</option>
                      <option value="simplyhired">SimplyHired</option>
                      <option value="jobbers">Jobbers.io</option>
                    </optgroup>
                  </select>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleJobSearch()}
                        placeholder="e.g. React developer"
                        className="w-full px-3 py-2.5 pr-10 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      />
                    </div>
                    <button
                      onClick={handleJobSearch}
                      disabled={loadingJobs}
                      className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 hover:bg-emerald-500 transition-colors"
                    >
                      {loadingJobs ? "..." : "Search"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Job List */}
            <div className="max-h-[400px] overflow-y-auto border-t border-white/5">
              {jobs.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">
                  {loadingJobs ? "Searching..." : "No jobs found. Try a different search."}
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {jobs.map((j) => {
                    const active = selectedJob?.id === j.id;
                    const jobStatus = proposalStatuses[j.id];
                    return (
                      <div
                        key={j.id}
                        className={`border-l-2 transition-colors ${
                          active ? "bg-emerald-500/10 border-emerald-500" : "hover:bg-white/5 border-transparent"
                        }`}
                      >
                        <button
                          onClick={() => setSelectedJob(j)}
                          className="w-full text-left px-4 pt-3 pb-1"
                        >
                          <p className={`font-medium text-sm truncate ${active ? "text-emerald-400" : "text-white"}`}>
                            {j.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className="text-gray-500">{j.platform_name || "Job"}</span>
                            {j.budget && j.budget !== "Not specified" && (
                              <>
                                <span className="text-gray-700">•</span>
                                <span className="text-emerald-400">{j.budget}</span>
                              </>
                            )}
                            {j.url && (
                              <>
                                <span className="text-gray-700">•</span>
                                <a href={j.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-cyan-400 hover:text-cyan-300 hover:underline">View →</a>
                              </>
                            )}
                          </div>
                        </button>
                        {jobStatus && jobStatus !== "pending" && (
                          <div className="flex items-center gap-1.5 px-4 pb-2.5 pt-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMarkProposal(j.id, jobStatus === "won" ? "pending" : "won"); }}
                              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${
                                jobStatus === "won"
                                  ? "bg-emerald-500 text-white"
                                  : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                              }`}
                            >
                              Won
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleMarkProposal(j.id, jobStatus === "lost" ? "pending" : "lost"); }}
                              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${
                                jobStatus === "lost"
                                  ? "bg-red-500 text-white"
                                  : "bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
                              }`}
                            >
                              Lost
                            </button>
                            <span className={`text-[10px] font-semibold ${jobStatus === "won" ? "text-emerald-400" : "text-red-400"}`}>
                              {jobStatus === "won" ? "Marked Won" : "Marked Lost"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Selected Job Details */}
          {selectedJob && (
            <div className="rounded-2xl border border-white/10 bg-[#0d1117] overflow-hidden">
              <button
                onClick={() => setShowJobDetails(!showJobDetails)}
                className="w-full px-4 py-3 border-b border-white/8 bg-black/30 flex items-center justify-between"
              >
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <span className="text-lg">📋</span>
                  Job Details
                </h3>
                <svg className={`w-4 h-4 text-gray-400 transition-transform ${showJobDetails ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showJobDetails && (
                <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
                  <div>
                    <p className="text-emerald-400 font-semibold text-sm">{selectedJob.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400">{selectedJob.platform_name}</span>
                      {selectedJob.budget && <span className="text-xs text-emerald-400">{selectedJob.budget}</span>}
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed whitespace-pre-wrap">
                    {selectedJob.description || "No description available."}
                  </p>
                  {selectedJob.url && (
                    <a
                      href={selectedJob.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300"
                    >
                      View original posting
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle Column: Customization */}
        <div className="lg:col-span-3 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d1117] overflow-hidden">
            <div className="px-4 py-3 border-b border-white/8 bg-black/30">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <span className="text-lg">⚙️</span>
                Customize Proposal
              </h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Profile Selector */}
              {allProfiles.length > 1 && (
                <div>
                  <label className="block text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Use Profile</label>
                  <select
                    value={selectedProfileId || ""}
                    onChange={(e) => setSelectedProfileId(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white focus:border-cyan-500/50 outline-none"
                  >
                    {allProfiles.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.profile_name}{p.is_active ? " (Active)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Tone */}
              <div>
                <label className="block text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Tone</label>
                <div className="grid grid-cols-2 gap-2">
                  {PROPOSAL_TONES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setProposalTone(t.id)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        proposalTone === t.id
                          ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                          : "bg-white/5 border border-white/10 text-gray-400 hover:border-white/20"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Length */}
              <div>
                <label className="block text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Length</label>
                <div className="flex gap-2">
                  {PROPOSAL_LENGTHS.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setProposalLength(l.id)}
                      className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        proposalLength === l.id
                          ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                          : "bg-white/5 border border-white/10 text-gray-400 hover:border-white/20"
                      }`}
                    >
                      <div>{l.label}</div>
                      <div className="text-[10px] opacity-60">{l.words}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Focus Areas */}
              <div>
                <label className="block text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">
                  Focus Areas <span className="text-gray-600">(max 3)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {FOCUS_AREAS.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => toggleFocusArea(f.id)}
                      className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                        focusAreas.includes(f.id)
                          ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                          : "bg-white/5 border border-white/10 text-gray-500 hover:border-white/20"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Notes */}
              <div>
                <label className="block text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">
                  Special Instructions
                </label>
                <textarea
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  placeholder="e.g., Mention my AWS certification, focus on startup experience, highlight React Native skills..."
                  className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-gray-100 placeholder:text-gray-600 text-xs resize-none h-20 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={() => runGenerateProposal(selectedJob)}
                disabled={loadingProposal || !selectedJob}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-bold text-sm disabled:opacity-50 hover:from-emerald-500 hover:to-cyan-500 transition-all flex items-center justify-center gap-2"
              >
                {loadingProposal ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Proposal
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="rounded-2xl border border-white/10 bg-[#0d1117] p-4">
            <h4 className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
              <span>💡</span> Pro Tips
            </h4>
            <ul className="space-y-2 text-xs text-gray-500">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>Complete your profile for personalized proposals</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>Always edit and personalize before sending</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>Reference specific details from the job posting</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">✓</span>
                <span>Keep it conversational, not corporate</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column: Proposal Output */}
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-[#0d1117] overflow-hidden h-full flex flex-col">
            <div className="px-4 py-3 border-b border-white/8 bg-black/30 flex items-center justify-between">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <span className="text-lg">📝</span>
                Your Proposal
                {proposal && <span className="text-xs text-gray-500 font-normal">({wordCount} words)</span>}
              </h3>
              <div className="flex items-center gap-2">
                {proposal && (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(!isEditing);
                        if (!isEditing) setEditedProposal(proposal);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        isEditing
                          ? "bg-amber-500/20 border border-amber-500/40 text-amber-400"
                          : "bg-white/5 border border-white/10 text-gray-400 hover:border-white/20"
                      }`}
                    >
                      {isEditing ? "Preview" : "Edit"}
                    </button>
                    <button
                      onClick={handleCopyProposal}
                      className="px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-xs font-medium hover:bg-emerald-500/30 transition-all flex items-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="flex-1 p-4">
              {isEditing ? (
                <textarea
                  value={editedProposal}
                  onChange={(e) => setEditedProposal(e.target.value)}
                  className="w-full h-full min-h-[500px] px-4 py-3 rounded-xl bg-black/40 border border-amber-500/30 text-gray-100 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                />
              ) : proposal ? (
                <div className="h-full min-h-[500px] px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-gray-100 text-sm leading-relaxed overflow-y-auto">
                  <div className="whitespace-pre-wrap">{proposal}</div>
                  {proposalSource && (
                    <p className="mt-3 pt-3 border-t border-white/10 text-[10px] text-gray-500">
                      Generated by <span className="text-gray-400 font-medium">{proposalSource}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-center p-6">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-emerald-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Ready to generate</h4>
                  <p className="text-gray-500 text-sm max-w-xs">
                    Select a job, customize your preferences, and click "Generate Proposal" to create a personalized, winning proposal.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Editor Modal */}
      {showProfileEditor && (
        <FreelancerProfileEditor
          onClose={() => setShowProfileEditor(false)}
          onSave={handleProfileSave}
        />
      )}

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 6px; }
        .custom-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.15);
          border-radius: 6px;
        }
      `}</style>
    </div>
  );
}
